import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Grid,
  GridItem,
  Heading,
  Icon,
  useMediaQuery,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { LastChangedTag } from "../../../../common/editing/LastChangedTag/LastChangedTag";
import { Layout } from "../../../../common/layout/Layout";
import { ProcessStepBar } from "../../../../common/layout/v2/ProcessStepBar";
import { useSub } from "../../../../common/subscription/useSub";
import { useQueryParams } from "../../../../common/useQueryParams";
import { CONTENT_WIDTH, requireBooleanEnvVar } from "../../../../config";
import { urls } from "../../../../urls";
import { EventStatusTag } from "../../EventStatusTag";
import { useBasicSourcingEventState } from "../../useBasicSourcingEventState";
import styles from "./EditBasicSourcingEventForm.module.css";
import { Navigation } from "./Navigation";
import { Docs } from "./docs/Docs";
import { Espd } from "./espd/Espd";
import { Intro } from "./intro/Intro";
import { InvitedParties } from "./parties/InvitedParties";
import { Products } from "./products/ProductsPage";
import { Review } from "./review/Review";

export const EditBasicSourcingEventForm = ({ embedded = false }: { embedded?: boolean }) => {
  const params = useQueryParams();
  const currentStep = params.get("step");
  const [showDrawer, setShowDrawer] = useState(false);
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");
  const navigate = useNavigate();
  const eventState = useBasicSourcingEventState();
  const subscriptions = useSub();

  useEffect(() => {
    if (!currentStep) {
      navigate(embedded ? `${urls.v2.sourcingEvents.bseEdit.go(eventState.id)}?step=intro` : urls.events.edit.go(eventState.id).intro);
    }
  }, [currentStep, eventState.id, navigate, embedded]);

  if (!currentStep) return <div>{t("Loading")}</div>;

  // In the embedded (v2) shell the step nav stays on the current /v2 path and only swaps
  // the ?step query, so the shell never remounts.
  const editPaths = urls.events.edit.go(eventState.id);
  const goToStep = (target: { pathname: string; search: string }) =>
    navigate(embedded ? { pathname: urls.v2.sourcingEvents.bseEdit.go(eventState.id), search: target.search } : target);
  const navigateToIntro = () => goToStep(editPaths.intro);
  const navigateToProducts = subscriptions.hasProductAccess ? () => goToStep(editPaths.products) : undefined;
  const navigateToDocs = () => goToStep(editPaths.docs);
  const navigateToEspd = requireBooleanEnvVar("VITE_ENABLE_ESPD") ? () => goToStep(editPaths.espd) : undefined;
  const navigateToParties = () => goToStep(editPaths.parties);
  const navigateToReview = () => goToStep(editPaths.review);
  const navigateToEventView =
    eventState.event.stateName !== "Draft"
      ? () =>
          navigate(embedded ? urls.v2.sourcingEvents.bseView.go(eventState.id) : urls.events.view.go(eventState.id))
      : undefined;

  const navigation = (
    <Navigation
      navigateToIntro={navigateToIntro}
      navigateToProducts={navigateToProducts}
      navigateToDocs={navigateToDocs}
      navigateToEspd={navigateToEspd}
      navigateToParties={navigateToParties}
      navigateToReview={navigateToReview}
      navigateToEventView={navigateToEventView}
    />
  );

  const stepContent = (
    <Box maxWidth="full">
      <Box display={currentStep === "intro" ? undefined : "none"}>
        <Intro nextStep={navigateToProducts ?? navigateToDocs} />
      </Box>
      <Box display={currentStep === "products" ? undefined : "none"} maxWidth="full">
        <Products previousStep={navigateToIntro} nextStep={navigateToDocs} />
      </Box>
      <Box display={currentStep === "docs" ? undefined : "none"}>
        <Docs previousStep={navigateToProducts ?? navigateToIntro} nextStep={navigateToEspd ?? navigateToParties} />
      </Box>
      {navigateToEspd && (
        <Box display={currentStep === "espd" ? undefined : "none"}>
          <Espd previousStep={navigateToDocs} nextStep={navigateToParties} eventId={eventState.id} />
        </Box>
      )}
      <Box display={currentStep === "parties" ? undefined : "none"}>
        <InvitedParties previousStep={navigateToEspd ?? navigateToDocs} nextStep={navigateToReview} />
      </Box>
      <Box display={currentStep === "review" ? undefined : "none"}>
        <Review previousStep={navigateToParties} />
      </Box>
    </Box>
  );

  if (embedded) {
    const stepNavigators: Record<string, (() => void) | undefined> = {
      intro: navigateToIntro,
      products: navigateToProducts,
      docs: navigateToDocs,
      espd: navigateToEspd,
      parties: navigateToParties,
      review: navigateToReview,
    };
    const barSteps = [
      { key: "intro", label: t("Introduction") },
      ...(navigateToProducts ? [{ key: "products", label: t("Products") }] : []),
      { key: "docs", label: t("Documents") },
      ...(navigateToEspd ? [{ key: "espd", label: t("ESPD") }] : []),
      { key: "parties", label: t("Invited parties") },
      { key: "review", label: t("Review") },
    ];

    return (
      <Box width="full" maxWidth="full">
        <Flex align="center" justify="space-between" gap="3" pt="2" wrap="wrap">
          <Box minW="0">
            <Heading as="h2" size="md" textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap" mb="1">
              {eventState.title.value}
            </Heading>
            <Flex columnGap="1">
              <EventStatusTag event={{ kind: "BSE", status: eventState.event.status }} />
              <LastChangedTag timestamp={eventState.lastChanged} />
            </Flex>
          </Box>
          {navigateToEventView && (
            <Button
              size="xs"
              variant="ghost"
              color="smMuted"
              leftIcon={<Icon as={FaArrowLeft} />}
              onClick={navigateToEventView}
            >
              {t("Back to event")}
            </Button>
          )}
        </Flex>
        <Box py="8" overflowX="auto">
          <ProcessStepBar
            maxW="full"
            steps={barSteps}
            currentKey={currentStep}
            onStepClick={(key) => stepNavigators[key]?.()}
          />
        </Box>
        {stepContent}
      </Box>
    );
  }

  return (
    <Layout>
      <Drawer placement={"bottom"} onClose={() => setShowDrawer(false)} isOpen={showDrawer}>
        <DrawerOverlay />
        <DrawerContent borderTop="1px solid" borderColor="smBorder">
          <DrawerBody>{navigation}</DrawerBody>
        </DrawerContent>
      </Drawer>
      <Flex
        display={isLargerThan768 ? "none" : "flex"}
        position="fixed"
        zIndex={1}
        bottom="0px"
        width="full"
        height="60px"
        borderTop={"1px solid"}
        borderColor="smBorder"
        alignItems="center"
        bg="smBackground"
      >
        <Button
          variant={"solid"}
          colorScheme="blue"
          width="full"
          ml="10px"
          mr="10px"
          onClick={() => setShowDrawer(true)}
        >
          {t("Change step")}
        </Button>
      </Flex>
      <Grid
        height="full"
        gridTemplateColumns={`1fr minmax(0, ${CONTENT_WIDTH}) 1fr`}
        gridTemplateRows={`auto minmax(min-content, auto)`}
        templateAreas={`
                "contentLeft content contentRight"
                `}
        width="full"
        maxWidth="full"
        overflowX="hidden"
      >
        <GridItem area="content" maxWidth="full" width="full">
          <Grid className={styles.grid} width="full" maxWidth="full" marginTop="9px" px="2">
            <GridItem
              area="menuNav"
              alignItems="end"
              borderBottom="1px solid"
              borderColor="smBorder"
              width="full"
              maxWidth="full"
            ></GridItem>
            <GridItem
              area="contentNav"
              alignItems="end"
              borderBottom="1px solid"
              borderColor="smBorder"
              overflow="hidden"
            >
              <Heading
                as="h2"
                size="md"
                width="full"
                textOverflow="ellipsis"
                overflow="hidden"
                whiteSpace="nowrap"
                mb="2"
              >
                {eventState.title.value}
              </Heading>
              <Flex columnGap="1">
                <EventStatusTag event={{ kind: "BSE", status: eventState.event.status }} />
                <LastChangedTag timestamp={eventState.lastChanged} />
              </Flex>
            </GridItem>
            <GridItem area="menu" display={isLargerThan768 ? "block" : "none"}>
              <Box pt="10">{navigation}</Box>
            </GridItem>
            <GridItem area="content" maxWidth="full" overflowX="hidden">
              {stepContent}
            </GridItem>
          </Grid>
        </GridItem>
      </Grid>
    </Layout>
  );
};
